import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminAlarmInfiniteList, adminAlarmKeys } from "../../../hooks/useAlarm";
import { markAdminAlarmAsRead, markAllAdminAlarmsAsRead } from "../../../utils/alarmService";
import { formatDateTime } from "../../../utils/commonService";
import { apiGet } from "../../../utils/apiClient";
import "./css/adminalarmmodal.css";
import { IoCaretDown } from "../../../components/icons";

const AdminAlarmModal = ({ onClose, onRead, buttonRef }) => {
    const [activeTab, setActiveTab] = useState("전체");
    const panelRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {
        data: alarmPages,
        isLoading: loading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useAdminAlarmInfiniteList();

    const alarmData = alarmPages?.pages.flatMap((page) => page.content) ?? [];

    const tabs = ["전체", "승인요청", "신고", "문의"];

    const getCategoryFromAlarmType = (alarmType) => {
        if (!alarmType) return "기타";
        if (alarmType === "가입") return "승인요청";
        if (alarmType === "신고") return "신고";
        if (alarmType === "문의") return "문의";
        return alarmType;
    };

    const transformedAlarms = alarmData.map((alarm) => ({
        alarmIdx: alarm.alarmIdx,
        category: getCategoryFromAlarmType(alarm.adminAlarmType),
        content: alarm.content || "알림 내용이 없습니다.",
        title: alarm.title,
        time: formatDateTime(alarm.sendDateTime),
        sendDateTime: alarm.sendDateTime,
        alarmLink: alarm.alarmLink,
        alarmType: alarm.adminAlarmType,
        isUnread: !alarm.readDateTime,
    }));

    const categoryFiltered =
        activeTab === "전체"
            ? transformedAlarms
            : transformedAlarms.filter((alarm) => alarm.category === activeTab);

    const unreadAlarms = categoryFiltered
        .filter((alarm) => alarm.isUnread)
        .sort((a, b) => new Date(b.sendDateTime) - new Date(a.sendDateTime));

    const readAlarms = categoryFiltered
        .filter((alarm) => !alarm.isUnread)
        .sort((a, b) => new Date(b.sendDateTime) - new Date(a.sendDateTime));

    const handleMarkAllRead = async () => {
        try {
            await markAllAdminAlarmsAsRead();
            queryClient.setQueryData(
                [...adminAlarmKeys.list(), "infinite"],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            content: page.content.map((a) => ({
                                ...a,
                                readDateTime: a.readDateTime ?? new Date().toISOString(),
                            })),
                        })),
                    };
                }
            );
            queryClient.invalidateQueries({ queryKey: adminAlarmKeys.list() });
            if (onRead) onRead();
        } catch {
            // silent
        }
    };

    const handleAlarmClick = async (alarm) => {
        if (alarm.isUnread) {
            try {
                await markAdminAlarmAsRead(alarm.alarmIdx);

                queryClient.setQueryData(
                    [...adminAlarmKeys.list(), "infinite"],
                    (old) => {
                        if (!old) return old;
                        return {
                            ...old,
                            pages: old.pages.map((page) => ({
                                ...page,
                                content: page.content.map((a) =>
                                    a.alarmIdx === alarm.alarmIdx
                                        ? { ...a, readDateTime: new Date().toISOString() }
                                        : a
                                ),
                            })),
                        };
                    }
                );

                queryClient.invalidateQueries({ queryKey: adminAlarmKeys.list() });
                if (onRead) onRead();
            } catch {
                // silent
            }
        }

        onClose();

        const link = alarm.alarmLink;
        const alarmType = alarm.alarmType;

        const checkAndNavigate = async () => {
            try {
                let checkUrl = null;
                let deletedMsg = null;

                if (alarmType === "문의") {
                    checkUrl = `/admin/question/${link}`;
                    deletedMsg = "삭제된 문의입니다.";
                } else if (alarmType === "신고") {
                    checkUrl = `/admin/report/${link}`;
                    deletedMsg = "삭제된 신고입니다.";
                } else if (alarmType === "가입") {
                    checkUrl = `/admin/store/detail/${link}`;
                    deletedMsg = "삭제된 가입 요청입니다.";
                }

                if (checkUrl) {
                    const res = await apiGet(checkUrl);
                    if (res.status === 404) {
                        alert(deletedMsg);
                        return;
                    }
                }

                switch (alarmType) {
                    case "가입":
                        navigate("/admin/accept");
                        break;
                    case "문의":
                        navigate("/admin/question", { state: { openModal: true, questionIdx: link } });
                        break;
                    case "신고":
                        navigate("/admin/report", { state: { openModal: true, reportIdx: link } });
                        break;
                    default:
                        break;
                }
            } catch {
                // 네트워크 오류 등은 그냥 이동
                switch (alarmType) {
                    case "가입":
                        navigate("/admin/accept");
                        break;
                    case "문의":
                        navigate("/admin/question", { state: { openModal: true, questionIdx: link } });
                        break;
                    case "신고":
                        navigate("/admin/report", { state: { openModal: true, reportIdx: link } });
                        break;
                    default:
                        break;
                }
            }
        };

        checkAndNavigate();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                if (buttonRef?.current && buttonRef.current.contains(event.target)) {
                    return;
                }
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose, buttonRef]);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const handleWheel = (e) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const atTop = scrollTop === 0 && e.deltaY < 0;
            const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
            if (atTop || atBottom) e.preventDefault();
            e.stopPropagation();
        };
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, []);

    return (
        <div className="adminalarmmodal-panel" ref={panelRef}>
            <div className="adminalarmmodal-tabs">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        className={`adminalarmmodal-tab-button ${activeTab === tab ? "adminalarmmodal-tab-active" : ""}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>
            {unreadAlarms.length > 0 && (
                <div className="adminalarmmodal-toolbar">
                    <span className="adminalarmmodal-unread-count">읽지 않은 알람 {unreadAlarms.length}개</span>
                    <button className="adminalarmmodal-readall-btn" onClick={handleMarkAllRead}>
                        전체 읽음
                    </button>
                </div>
            )}

            <div className="adminalarmmodal-list-container" ref={listRef}>
                {loading ? (
                    <div className="adminalarmmodal-empty">로딩 중...</div>
                ) : unreadAlarms.length === 0 && readAlarms.length === 0 ? (
                    <div className="adminalarmmodal-empty">알람이 없습니다.</div>
                ) : (
                    <>
                        {unreadAlarms.map((alarm) => (
                            <div
                                key={alarm.alarmIdx}
                                className="adminalarmmodal-item adminalarmmodal-item-unread"
                                onClick={() => handleAlarmClick(alarm)}
                            >
                                <div className="adminalarmmodal-item-header">
                                    <span className="adminalarmmodal-item-category">{alarm.category}</span>
                                    <span className="adminalarmmodal-item-time" style={{ fontWeight: "bold" }}>{alarm.time}</span>
                                </div>
                                {alarm.title && <div className="adminalarmmodal-item-title">{alarm.title}</div>}
                                <div className="adminalarmmodal-item-content" style={{ fontWeight: "bold" }}>{alarm.content}</div>
                            </div>
                        ))}

                        {unreadAlarms.length > 0 && readAlarms.length > 0 && (
                            <div className="adminalarmmodal-divider">
                                <span className="adminalarmmodal-divider-text">읽은 알람</span>
                            </div>
                        )}

                        {readAlarms.map((alarm) => (
                            <div
                                key={alarm.alarmIdx}
                                className="adminalarmmodal-item"
                                onClick={() => handleAlarmClick(alarm)}
                            >
                                <div className="adminalarmmodal-item-header">
                                    <span className="adminalarmmodal-item-category">{alarm.category}</span>
                                    <span className="adminalarmmodal-item-time">{alarm.time}</span>
                                </div>
                                {alarm.title && <div className="adminalarmmodal-item-title">{alarm.title}</div>}
                                <div className="adminalarmmodal-item-content">{alarm.content}</div>
                            </div>
                        ))}
                    </>
                )}

                {hasNextPage && (
                    <button
                        className="adminalarmmodal-more-btn"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? "로딩 중..." : <><div>더보기</div><IoCaretDown /></>}
                    </button>
                )}
            </div>
        </div>
    );
};

export default AdminAlarmModal;